import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { authenticatedFetch } from "../../utils/api";
import Navbar from "../Navbar";
import TalentCards from "../TalentCards";
import TalentDetailedReports from "../TalentDetailedReports";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const CompanyDashboard = () => {
  const { user } = useAuth();
  const { showToast } = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState("company");

  // Company tab states
  const [companyStats, setCompanyStats] = useState(null);
  const [companyAnalysis, setCompanyAnalysis] = useState(null);
  const [companySurveys, setCompanySurveys] = useState(null);
  const [companyWeeklyAI, setCompanyWeeklyAI] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyStatsCache, setCompanyStatsCache] = useState({}); // Cache for company stats by page
  const [companyStatsPaginationLoading, setCompanyStatsPaginationLoading] =
    useState(false);
  const [
    companyAnalysisPaginationLoading,
    setCompanyAnalysisPaginationLoading,
  ] = useState(false);
  const [companySurveysPaginationLoading, setCompanySurveysPaginationLoading] =
    useState(false);
  const [
    companyWeeklyAIPaginationLoading,
    setCompanyWeeklyAIPaginationLoading,
  ] = useState(false);

  // Companies dropdown states
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState(null);

  // Company stats pagination
  const [companyStatsPagination, setCompanyStatsPagination] = useState({
    page: 1,
    page_size: 6,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  // Company analysis pagination
  const [companyAnalysisPagination, setCompanyAnalysisPagination] = useState({
    page: 1,
    page_size: 6,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  // Company surveys pagination
  const [companySurveysPagination, setCompanySurveysPagination] = useState({
    page: 1,
    page_size: 6,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  // Company weekly AI pagination
  const [companyWeeklyAIPagination, setCompanyWeeklyAIPagination] = useState({
    page: 1,
    page_size: 6,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  // Talent tab states
  const [talentData, setTalentData] = useState(null);
  const [talentWeeklyAI, setTalentWeeklyAI] = useState(null);
  const [talentAllUsers, setTalentAllUsers] = useState(null);
  const [talentLoading, setTalentLoading] = useState(false);
  const [talentError, setTalentError] = useState(null);
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [selectedUserForWeeklyAI, setSelectedUserForWeeklyAI] = useState(null);
  const [weeklyAILoading, setWeeklyAILoading] = useState(false);

  // Talent company filter
  const [selectedTalentCompanyFilter, setSelectedTalentCompanyFilter] =
    useState(null);

  // Talent pagination states
  const [talentDataPagination, setTalentDataPagination] = useState({
    page: 1,
    page_size: 6,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    _total_pages_provided: false, // Track if total_pages was provided by API
  });

  const [talentAllUsersPagination, setTalentAllUsersPagination] = useState({
    page: 1,
    page_size: 6,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    _total_pages_provided: false, // Track if total_pages was provided by API
  });

  // Talent pagination loading states
  const [talentDataPaginationLoading, setTalentDataPaginationLoading] =
    useState(false);
  const [talentAllUsersPaginationLoading, setTalentAllUsersPaginationLoading] =
    useState(false);

  // Survey details modal states
  const [surveyDetails, setSurveyDetails] = useState(null);
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Survey data modal states
  const [surveyData, setSurveyData] = useState(null);
  const [surveyDataModalOpen, setSurveyDataModalOpen] = useState(false);
  const [surveyDataLoading, setSurveyDataLoading] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [loadingSurveyId, setLoadingSurveyId] = useState(null);

  // Fetch companies from API
  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const url = "https://pulse.upstartaec.com/agent/candidates/companies";
      const response = await authenticatedFetch(url);
      if (response && response.success) {
        setCompanies(response.companies || []);
      } else {
        throw new Error(response.message || "Failed to fetch companies");
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      showToast(error.message || "Failed to fetch companies", "error");
    } finally {
      setLoadingCompanies(false);
    }
  }, [showToast]);

  // Fetch company-wide user stats
  const fetchCompanyStats = useCallback(
    async (page = 1, pageSize = 6, company = null, useCache = true) => {
      // Check cache first (only if no company filter)
      const cacheKey = company ? `${page}_${company}` : page;
      if (useCache && !company && companyStatsCache[page]) {
        // Clear loading state if it was set
        setCompanyStatsPaginationLoading(false);
        setCompanyStats(companyStatsCache[page]);
        const cachedData = companyStatsCache[page];
        if (cachedData.global_stats) {
          const totalCompanies = cachedData.global_stats.total_companies || 0;
          const totalPages = Math.ceil(totalCompanies / pageSize);
          setCompanyStatsPagination({
            page: page,
            page_size: pageSize,
            total_pages: totalPages,
            has_next: page < totalPages,
            has_previous: page > 1,
          });
        }
        return;
      }

      // If not in cache, fetch from API (loading already set in handler)
      try {
        let url = `https://pulse.upstartaec.com/agent/dashboard/company-wide-user-stats?page=${page}&page_size=${pageSize}`;
        if (company) {
          url += `&company=${encodeURIComponent(company)}`;
        }
        const response = await authenticatedFetch(url);
        if (response.success) {
          // Store in cache only if no company filter
          if (!company) {
            setCompanyStatsCache((prev) => ({
              ...prev,
              [page]: response,
            }));
          }
          setCompanyStats(response);
          // Calculate pagination state from response
          if (response.global_stats) {
            const totalCompanies = response.global_stats.total_companies || 0;
            const totalPages = Math.ceil(totalCompanies / pageSize);
            setCompanyStatsPagination({
              page: page,
              page_size: pageSize,
              total_pages: totalPages,
              has_next: page < totalPages,
              has_previous: page > 1,
            });
          }
        } else {
          throw new Error(response.message || "Failed to fetch company stats");
        }
      } catch (error) {
        console.error("Error fetching company stats:", error);
        throw error;
      } finally {
        setCompanyStatsPaginationLoading(false);
      }
    },
    [companyStatsCache]
  );

  // Fetch company-wide analysis report
  const fetchCompanyAnalysis = useCallback(
    async (page = 1, pageSize = 6, company = null) => {
      try {
        let url = `https://pulse.upstartaec.com/agent/dashboard/company-wide-analysis-report?page=${page}&page_size=${pageSize}`;
        if (company) {
          url += `&company=${encodeURIComponent(company)}`;
        }
        const response = await authenticatedFetch(url);
        if (response.success) {
          setCompanyAnalysis(response);
          // Update pagination state from response
          // API returns hasNext and hasPrev, but may not have total_pages
          const hasNext = response.hasNext || false;
          const hasPrev = response.hasPrev || false;

          setCompanyAnalysisPagination({
            page: page,
            page_size: pageSize,
            // If total_pages not provided, estimate based on current page and hasNext
            // This is just for display - actual navigation uses hasNext/hasPrev
            total_pages: response.total_pages || (hasNext ? page + 1 : page),
            has_next: hasNext,
            has_previous: hasPrev,
          });
        } else {
          throw new Error(
            response.message || "Failed to fetch company analysis"
          );
        }
      } catch (error) {
        console.error("Error fetching company analysis:", error);
        throw error;
      }
    },
    []
  );

  // Fetch company-wide surveys stats
  const fetchCompanySurveys = useCallback(
    async (page = 1, pageSize = 6, company = null) => {
      try {
        let url = `https://pulse.upstartaec.com/agent/dashboard/company-wide-surveys-stats?page=${page}&page_size=${pageSize}`;
        if (company) {
          url += `&company=${encodeURIComponent(company)}`;
        }
        const response = await authenticatedFetch(url);
        if (response.success) {
          setCompanySurveys(response);
          // Update pagination state from response
          setCompanySurveysPagination({
            page: page,
            page_size: pageSize,
            total_pages: response.total_pages || 1,
            has_next: response.hasNext || false,
            has_previous: response.hasPrev || false,
          });
          // Auto-select the first company by default if no company filter
          if (
            !company &&
            response.per_company_stats &&
            response.per_company_stats.length > 0
          ) {
            setSelectedCompany(response.per_company_stats[0]);
          }
        } else {
          throw new Error(
            response.message || "Failed to fetch company surveys"
          );
        }
      } catch (error) {
        console.error("Error fetching company surveys:", error);
        throw error;
      }
    },
    []
  );

  // Fetch company-wide weekly AI question analytics
  const fetchCompanyWeeklyAI = useCallback(
    async (page = 1, pageSize = 6, company = null) => {
      try {
        let url = `https://pulse.upstartaec.com/agent/dashboard/company-wide-weekly-ai-question-analytics?page=${page}&page_size=${pageSize}`;
        if (company) {
          url += `&company=${encodeURIComponent(company)}`;
        }
        const response = await authenticatedFetch(url);
        if (response.success) {
          setCompanyWeeklyAI(response);
          // Update pagination state from response
          setCompanyWeeklyAIPagination({
            page: page,
            page_size: pageSize,
            total_pages: response.total_pages || 1,
            has_next: response.hasNext || false,
            has_previous: response.hasPrev || false,
          });
        } else {
          throw new Error(
            response.message || "Failed to fetch weekly AI analytics"
          );
        }
      } catch (error) {
        console.error("Error fetching weekly AI analytics:", error);
        throw error;
      }
    },
    []
  );

  // Fetch talent analysis report
  const fetchTalentData = useCallback(
    async (page = 1, pageSize = 6, company = null) => {
      try {
        let url = `https://pulse.upstartaec.com/agent/dashboard/user-level-analysis-report?page=${page}&page_size=${pageSize}`;
        if (company) {
          url += `&company=${encodeURIComponent(company)}`;
        }
        const response = await authenticatedFetch(url);
        if (response.success) {
          setTalentData(response);
          // API returns hasNext and hasPrev, but may not have total_pages
          const hasNext = response.hasNext || false;
          const hasPrev = response.hasPrev || false;
          // Estimate total_pages if not provided (for display purposes)
          // If hasNext is true, we know there are more pages
          let estimatedTotalPages = response.total_pages;
          if (!estimatedTotalPages && hasNext) {
            // If we have hasNext but no total_pages, estimate based on current page
            estimatedTotalPages = page + 1; // At least one more page
          } else if (!estimatedTotalPages) {
            estimatedTotalPages = page; // Current page is the last
          }
          // Update pagination state from response
          // Store whether total_pages was provided by API (for display logic)
          setTalentDataPagination({
            page: page,
            page_size: pageSize,
            total_pages: estimatedTotalPages || 1,
            has_next: hasNext,
            has_previous: hasPrev,
            // Track if total_pages was provided by API (not estimated)
            _total_pages_provided: !!response.total_pages,
          });
          // Auto-select first talent if users exist
          if (response.data && response.data.length > 0) {
            setSelectedTalent(response.data[0]);
          }
        } else {
          throw new Error(response.message || "Failed to fetch talent data");
        }
      } catch (error) {
        console.error("Error fetching talent data:", error);
        throw error;
      }
    },
    []
  );

  // Fetch talent weekly AI question analytics
  const fetchTalentWeeklyAI = useCallback(async (userId) => {
    if (!userId) return;

    setWeeklyAILoading(true);
    try {
      const params = new URLSearchParams({
        user_id: userId,
      });

      const response = await authenticatedFetch(
        `https://pulse.upstartaec.com/agent/dashboard/weekly-ai-question-analytics?${params.toString()}`
      );
      if (response.success) {
        setTalentWeeklyAI(response);
      } else {
        throw new Error(
          response.message || "Failed to fetch talent weekly AI analytics"
        );
      }
    } catch (error) {
      console.error("Error fetching talent weekly AI analytics:", error);
      throw error;
    } finally {
      setWeeklyAILoading(false);
    }
  }, []);

  // Fetch all users data for talent tab
  const fetchTalentAllUsers = useCallback(
    async (page = 1, pageSize = 6, company = null) => {
      try {
        let url = `https://pulse.upstartaec.com/agent/dashboard/all-users?page=${page}&page_size=${pageSize}`;
        if (company) {
          url += `&company=${encodeURIComponent(company)}`;
        }
        const response = await authenticatedFetch(url);
        if (response.success) {
          setTalentAllUsers(response);
          // API returns hasNext and hasPrev, but may not have total_pages
          const hasNext = response.hasNext || false;
          const hasPrev = response.hasPrev || false;
          // Estimate total_pages if not provided (for display purposes)
          // If hasNext is true, we know there are more pages
          let estimatedTotalPages = response.total_pages;
          if (!estimatedTotalPages && hasNext) {
            // If we have hasNext but no total_pages, estimate based on current page
            estimatedTotalPages = page + 1; // At least one more page
          } else if (!estimatedTotalPages) {
            estimatedTotalPages = page; // Current page is the last
          }
          // Update pagination state from response
          setTalentAllUsersPagination({
            page: page,
            page_size: pageSize,
            total_pages: estimatedTotalPages || 1,
            has_next: hasNext,
            has_previous: hasPrev,
            // Track if total_pages was provided by API (not estimated)
            _total_pages_provided: !!response.total_pages,
          });
        } else {
          throw new Error(response.message || "Failed to fetch all users data");
        }
      } catch (error) {
        console.error("Error fetching all users data:", error);
        throw error;
      }
    },
    []
  );

  // Fetch survey details for a specific user
  const fetchSurveyDetails = useCallback(
    async (userId) => {
      try {
        setSurveyLoading(true);
        const response = await authenticatedFetch(
          `https://pulse.upstartaec.com/agent/dashboard/survey-detail/${userId}`
        );
        if (response.success) {
          setSurveyDetails(response);
          setSurveyModalOpen(true);
        } else {
          throw new Error(response.message || "Failed to fetch survey details");
        }
      } catch (error) {
        console.error("Error fetching survey details:", error);
        showToast(error.message || "Failed to fetch survey details", "error");
      } finally {
        setSurveyLoading(false);
      }
    },
    [showToast]
  );

  // Fetch survey data for a specific survey
  const fetchSurveyData = useCallback(
    async (surveyId, userId) => {
      try {
        setLoadingSurveyId(surveyId);
        setSurveyDataLoading(true);
        const response = await authenticatedFetch(
          `https://pulse.upstartaec.com/agent/survey-generation/get-survey-data/${surveyId}?user_id=${userId}`
        );
        if (response.success) {
          setSurveyData(response);
          setSurveyDataModalOpen(true);
        } else {
          throw new Error(response.message || "Failed to fetch survey data");
        }
      } catch (error) {
        console.error("Error fetching survey data:", error);
        showToast(error.message || "Failed to fetch survey data", "error");
      } finally {
        setSurveyDataLoading(false);
        setLoadingSurveyId(null);
      }
    },
    [showToast]
  );

  // Load company data
  const loadCompanyData = useCallback(async () => {
    if (companyLoading) return;

    setCompanyLoading(true);
    setCompanyError(null);

    try {
      // Access pagination values directly from state (they're always current)
      // This prevents the callback from being recreated on every pagination change
      await Promise.all([
        fetchCompanyStats(
          companyStatsPagination.page,
          companyStatsPagination.page_size,
          selectedCompanyFilter,
          false // Don't use cache on initial load
        ),
        fetchCompanyAnalysis(
          companyAnalysisPagination.page,
          companyAnalysisPagination.page_size,
          selectedCompanyFilter
        ),
        fetchCompanySurveys(
          companySurveysPagination.page,
          companySurveysPagination.page_size,
          selectedCompanyFilter
        ),
        fetchCompanyWeeklyAI(
          companyWeeklyAIPagination.page,
          companyWeeklyAIPagination.page_size,
          selectedCompanyFilter
        ),
      ]);
    } catch (error) {
      const errorMessage = error.message || "Failed to load company data";
      setCompanyError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setCompanyLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyLoading,
    fetchCompanyStats,
    fetchCompanyAnalysis,
    fetchCompanySurveys,
    fetchCompanyWeeklyAI,
    showToast,
    selectedCompanyFilter,
    // Removed pagination pages from deps - we access them directly from state
    // This prevents infinite loops when pagination changes
  ]);

  // Handle company stats pagination
  const handleCompanyStatsPageChange = useCallback(
    (newPage) => {
      if (newPage >= 1 && newPage <= companyStatsPagination.total_pages) {
        // Set loading state immediately (synchronously) before async operation
        setCompanyStatsPaginationLoading(true);

        // Check if data is in cache - if not, set loading immediately for instant UI feedback
        const isCached = !selectedCompanyFilter && companyStatsCache[newPage];

        // Call fetch function (it will handle cache check and API call)
        fetchCompanyStats(
          newPage,
          companyStatsPagination.page_size,
          selectedCompanyFilter
        ).finally(() => {
          setCompanyStatsPaginationLoading(false);
        });
      }
    },
    [
      fetchCompanyStats,
      companyStatsPagination.total_pages,
      companyStatsPagination.page_size,
      companyStatsCache,
      selectedCompanyFilter,
    ]
  );

  // Handle company analysis pagination
  const handleCompanyAnalysisPageChange = useCallback(
    (newPage) => {
      // Allow page change if page >= 1 and either has next/prev or it's a valid navigation
      if (newPage >= 1) {
        // Set loading state immediately for instant UI feedback
        setCompanyAnalysisPaginationLoading(true);
        fetchCompanyAnalysis(
          newPage,
          companyAnalysisPagination.page_size,
          selectedCompanyFilter
        ).finally(() => {
          setCompanyAnalysisPaginationLoading(false);
        });
      }
    },
    [
      fetchCompanyAnalysis,
      companyAnalysisPagination.page_size,
      selectedCompanyFilter,
    ]
  );

  // Handle company surveys pagination
  const handleCompanySurveysPageChange = useCallback(
    (newPage) => {
      if (newPage >= 1 && newPage <= companySurveysPagination.total_pages) {
        // Set loading state immediately for instant UI feedback
        setCompanySurveysPaginationLoading(true);
        fetchCompanySurveys(
          newPage,
          companySurveysPagination.page_size,
          selectedCompanyFilter
        ).finally(() => {
          setCompanySurveysPaginationLoading(false);
        });
      }
    },
    [
      fetchCompanySurveys,
      companySurveysPagination.total_pages,
      companySurveysPagination.page_size,
      selectedCompanyFilter,
    ]
  );

  // Handle company weekly AI pagination
  const handleCompanyWeeklyAIPageChange = useCallback(
    (newPage) => {
      if (newPage >= 1 && newPage <= companyWeeklyAIPagination.total_pages) {
        // Set loading state immediately for instant UI feedback
        setCompanyWeeklyAIPaginationLoading(true);
        fetchCompanyWeeklyAI(
          newPage,
          companyWeeklyAIPagination.page_size,
          selectedCompanyFilter
        ).finally(() => {
          setCompanyWeeklyAIPaginationLoading(false);
        });
      }
    },
    [
      fetchCompanyWeeklyAI,
      companyWeeklyAIPagination.total_pages,
      companyWeeklyAIPagination.page_size,
      selectedCompanyFilter,
    ]
  );

  // Handle company filter change
  const handleCompanyFilterChange = useCallback((company) => {
    setSelectedCompanyFilter(company);
    // Reset all paginations to page 1
    setCompanyStatsPagination((prev) => ({ ...prev, page: 1 }));
    setCompanyAnalysisPagination((prev) => ({ ...prev, page: 1 }));
    setCompanySurveysPagination((prev) => ({ ...prev, page: 1 }));
    setCompanyWeeklyAIPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Handle talent company filter change
  const handleTalentCompanyFilterChange = useCallback((company) => {
    setSelectedTalentCompanyFilter(company);
    // Reset all talent paginations to page 1
    setTalentDataPagination((prev) => ({ ...prev, page: 1 }));
    setTalentAllUsersPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Handle talent data pagination
  const handleTalentDataPageChange = useCallback(
    (newPage) => {
      if (newPage >= 1) {
        setTalentDataPaginationLoading(true);
        fetchTalentData(
          newPage,
          talentDataPagination.page_size,
          selectedTalentCompanyFilter
        ).finally(() => {
          setTalentDataPaginationLoading(false);
        });
      }
    },
    [
      fetchTalentData,
      talentDataPagination.page_size,
      selectedTalentCompanyFilter,
    ]
  );

  // Handle talent all users pagination
  const handleTalentAllUsersPageChange = useCallback(
    (newPage) => {
      if (newPage >= 1) {
        setTalentAllUsersPaginationLoading(true);
        fetchTalentAllUsers(
          newPage,
          talentAllUsersPagination.page_size,
          selectedTalentCompanyFilter
        ).finally(() => {
          setTalentAllUsersPaginationLoading(false);
        });
      }
    },
    [
      fetchTalentAllUsers,
      talentAllUsersPagination.page_size,
      selectedTalentCompanyFilter,
    ]
  );

  // Load talent data
  const loadTalentData = useCallback(async () => {
    if (talentLoading) return;

    setTalentLoading(true);
    setTalentError(null);

    try {
      await Promise.all([
        fetchTalentData(
          talentDataPagination.page,
          talentDataPagination.page_size,
          selectedTalentCompanyFilter
        ),
        fetchTalentAllUsers(
          talentAllUsersPagination.page,
          talentAllUsersPagination.page_size,
          selectedTalentCompanyFilter
        ),
      ]);
    } catch (error) {
      const errorMessage = error.message || "Failed to load talent data";
      setTalentError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setTalentLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    talentLoading,
    fetchTalentData,
    fetchTalentAllUsers,
    showToast,
    selectedTalentCompanyFilter,
    // Removed pagination pages from deps - we access them directly from state
  ]);

  // Delete talent(s) by user_id
  const handleDeleteTalent = useCallback(
    async (userId) => {
      if (!userId) return;

      const confirmed = window.confirm(
        "Are you sure you want to delete this talent? This action cannot be undone."
      );
      if (!confirmed) return;

      try {
        // Send the payload as a JSON array of user ids (some endpoints expect a list body)
        const response = await authenticatedFetch(
          "https://pulse.upstartaec.com/agent/candidates/talents",
          {
            method: "DELETE",
            body: JSON.stringify([userId]),
          }
        );

        if (response && response.success) {
          showToast(response.message || "Talent deleted", "success");
          // Refresh talent data
          await loadTalentData();
        } else {
          showToast(response.message || "Failed to delete talent", "error");
        }
      } catch (error) {
        console.error("Error deleting talent:", error);
        showToast(error.message || "Failed to delete talent", "error");
      }
    },
    [loadTalentData, showToast]
  );

  // Handle user selection for weekly AI analytics
  const handleUserSelectionForWeeklyAI = useCallback(
    async (user) => {
      setSelectedUserForWeeklyAI(user);
      await fetchTalentWeeklyAI(user.user_id);
    },
    [fetchTalentWeeklyAI]
  );

  // Set first user as default and fetch their weekly AI data when talentAllUsers loads
  useEffect(() => {
    if (
      talentAllUsers &&
      talentAllUsers.users &&
      talentAllUsers.users.length > 0 &&
      !selectedUserForWeeklyAI
    ) {
      const firstUser = talentAllUsers.users[0];
      setSelectedUserForWeeklyAI(firstUser);
      fetchTalentWeeklyAI(firstUser.user_id);
    }
  }, [talentAllUsers, selectedUserForWeeklyAI, fetchTalentWeeklyAI]);

  // Fetch companies on mount (only once when tab changes to company or talent)
  useEffect(() => {
    if (
      (activeTab === "company" || activeTab === "talent") &&
      companies.length === 0 &&
      !loadingCompanies
    ) {
      fetchCompanies();
    }
  }, [activeTab]); // Removed fetchCompanies from deps to prevent re-fetching

  // Track previous company filter and tab to detect changes
  const prevCompanyFilterRef = useRef(null);
  const prevTalentCompanyFilterRef = useRef(null);
  const prevTabRef = useRef(null);

  // Load data when tab changes or company filter changes (not on pagination changes)
  useEffect(() => {
    if (activeTab === "company") {
      const tabChanged = prevTabRef.current !== activeTab;
      const companyFilterChanged =
        prevCompanyFilterRef.current !== selectedCompanyFilter;

      // Load if:
      // 1. Tab just changed to company (initial load)
      // 2. Company filter changed
      if (tabChanged || companyFilterChanged) {
        prevTabRef.current = activeTab;
        prevCompanyFilterRef.current = selectedCompanyFilter;
        loadCompanyData();
      }
    } else if (activeTab === "talent") {
      const tabChanged = prevTabRef.current !== activeTab;
      const talentCompanyFilterChanged =
        prevTalentCompanyFilterRef.current !== selectedTalentCompanyFilter;

      // Load if:
      // 1. Tab just changed to talent (initial load)
      // 2. Company filter changed
      if (tabChanged || talentCompanyFilterChanged) {
        prevTabRef.current = activeTab;
        prevTalentCompanyFilterRef.current = selectedTalentCompanyFilter;
        loadTalentData();
      }
    }

    // Update tab ref
    if (prevTabRef.current !== activeTab) {
      prevTabRef.current = activeTab;
    }
  }, [
    activeTab,
    selectedCompanyFilter,
    selectedTalentCompanyFilter,
    // Removed loadCompanyData and loadTalentData from deps to prevent recreation loops
    // They are stable callbacks but we'll call them directly
    talentData,
    talentLoading,
  ]);

  // Show loading state while user is being fetched
  if (!user || !user.id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
                <p className="mt-2 text-gray-600">
                  Comprehensive analytics and insights
                </p>
              </div>
              {/* Company Filter - Show on Company and Talent tabs */}
              {(activeTab === "company" || activeTab === "talent") && (
                <div className="flex items-center gap-4">
                  <label
                    htmlFor="company-filter"
                    className="text-sm font-medium text-gray-700 whitespace-nowrap"
                  >
                    Filter by Company:
                  </label>
                  <select
                    id="company-filter"
                    value={
                      activeTab === "company"
                        ? selectedCompanyFilter || ""
                        : selectedTalentCompanyFilter || ""
                    }
                    onChange={(e) => {
                      if (activeTab === "company") {
                        handleCompanyFilterChange(e.target.value || null);
                      } else {
                        handleTalentCompanyFilterChange(e.target.value || null);
                      }
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white min-w-[200px]"
                    disabled={loadingCompanies}
                  >
                    <option value="">All Companies</option>
                    {loadingCompanies ? (
                      <option disabled>Loading companies...</option>
                    ) : (
                      companies.map((companyItem) => (
                        <option
                          key={companyItem.company}
                          value={companyItem.company}
                        >
                          {companyItem.company}
                          {companyItem.candidate_count !== undefined &&
                            ` (${companyItem.candidate_count})`}
                        </option>
                      ))
                    )}
                  </select>
                  {((activeTab === "company" && selectedCompanyFilter) ||
                    (activeTab === "talent" &&
                      selectedTalentCompanyFilter)) && (
                    <button
                      onClick={() => {
                        if (activeTab === "company") {
                          handleCompanyFilterChange(null);
                        } else {
                          handleTalentCompanyFilterChange(null);
                        }
                      }}
                      className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-8">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("company")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "company"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    <span>Company</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab("talent")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "talent"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                      />
                    </svg>
                    <span>Talent</span>
                  </div>
                </button>
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === "company" ? (
            <CompanyTab
              companyStats={companyStats}
              companyAnalysis={companyAnalysis}
              companySurveys={companySurveys}
              companyWeeklyAI={companyWeeklyAI}
              loading={companyLoading}
              error={companyError}
              onRefresh={loadCompanyData}
              selectedCompany={selectedCompany}
              setSelectedCompany={setSelectedCompany}
              pagination={companyStatsPagination}
              onPageChange={handleCompanyStatsPageChange}
              paginationLoading={companyStatsPaginationLoading}
              analysisPagination={companyAnalysisPagination}
              onAnalysisPageChange={handleCompanyAnalysisPageChange}
              analysisPaginationLoading={companyAnalysisPaginationLoading}
              surveysPagination={companySurveysPagination}
              onSurveysPageChange={handleCompanySurveysPageChange}
              surveysPaginationLoading={companySurveysPaginationLoading}
              weeklyAIPagination={companyWeeklyAIPagination}
              onWeeklyAIPageChange={handleCompanyWeeklyAIPageChange}
              weeklyAIPaginationLoading={companyWeeklyAIPaginationLoading}
            />
          ) : (
            <TalentTab
              talentData={talentData}
              talentWeeklyAI={talentWeeklyAI}
              talentAllUsers={talentAllUsers}
              loading={talentLoading}
              error={talentError}
              onRefresh={loadTalentData}
              selectedTalent={selectedTalent}
              setSelectedTalent={setSelectedTalent}
              onUserClick={fetchSurveyDetails}
              surveyLoading={surveyLoading}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              onSurveyClick={fetchSurveyData}
              selectedUserForWeeklyAI={selectedUserForWeeklyAI}
              onUserSelectionForWeeklyAI={handleUserSelectionForWeeklyAI}
              onTalentDelete={handleDeleteTalent}
              weeklyAILoading={weeklyAILoading}
              talentDataPagination={talentDataPagination}
              onTalentDataPageChange={handleTalentDataPageChange}
              talentDataPaginationLoading={talentDataPaginationLoading}
              talentAllUsersPagination={talentAllUsersPagination}
              onTalentAllUsersPageChange={handleTalentAllUsersPageChange}
              talentAllUsersPaginationLoading={talentAllUsersPaginationLoading}
            />
          )}
        </div>
      </div>

      {/* Survey Details Modal */}
      {surveyModalOpen && (
        <SurveyDetailsModal
          surveyDetails={surveyDetails}
          selectedUser={selectedUser}
          onClose={() => {
            setSurveyModalOpen(false);
            setSurveyDetails(null);
            setSelectedUser(null);
          }}
          loading={surveyLoading}
          onSurveyClick={fetchSurveyData}
          loadingSurveyId={loadingSurveyId}
        />
      )}

      {/* Survey Data Modal */}
      {surveyDataModalOpen && (
        <SurveyDataModal
          surveyData={surveyData}
          selectedSurvey={selectedSurvey}
          onClose={() => {
            setSurveyDataModalOpen(false);
            setSurveyData(null);
            setSelectedSurvey(null);
          }}
          loading={surveyDataLoading}
        />
      )}
    </>
  );
};

// Company Tab Component
const CompanyTab = ({
  companyStats,
  companyAnalysis,
  companySurveys,
  companyWeeklyAI,
  loading,
  error,
  onRefresh,
  selectedCompany,
  setSelectedCompany,
  pagination,
  onPageChange,
  paginationLoading,
  analysisPagination,
  onAnalysisPageChange,
  analysisPaginationLoading,
  surveysPagination,
  onSurveysPageChange,
  surveysPaginationLoading,
  weeklyAIPagination,
  onWeeklyAIPageChange,
  weeklyAIPaginationLoading,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading company data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-600 mb-4">
          <svg
            className="w-12 h-12 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-red-800 mb-2">
          Error Loading Data
        </h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={onRefresh}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Company Stats Overview */}
      {companyStats && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Company Overview
            </h2>
            <button
              onClick={onRefresh}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              Refresh
            </button>
          </div>

          {/* Global Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-blue-600">
                    Total Companies
                  </p>
                  <p className="text-2xl font-bold text-blue-900">
                    {companyStats.global_stats?.total_companies || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-green-600">
                    Total Users
                  </p>
                  <p className="text-2xl font-bold text-green-900">
                    {companyStats.global_stats?.total_users || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg
                    className="w-6 h-6 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-purple-600">
                    Total Surveys
                  </p>
                  <p className="text-2xl font-bold text-purple-900">
                    {companyStats.global_stats?.total_surveys || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-200">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <svg
                    className="w-6 h-6 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-orange-600">
                    Completion Rate
                  </p>
                  <p className="text-2xl font-bold text-orange-900">
                    {companyStats.global_stats?.global_completion_rate?.toFixed(
                      1
                    ) || 0}
                    %
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Company Details */}
          <div className="space-y-4 relative">
            <h3 className="text-lg font-medium text-gray-900">
              Company Details
            </h3>
            <div className="relative">
              {paginationLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Loading...</p>
                  </div>
                </div>
              )}
              <div
                className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${
                  paginationLoading ? "opacity-50" : ""
                }`}
              >
                {companyStats.companies?.map((company, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {company.name}
                      </h4>
                      <span className="text-sm text-gray-500">
                        Completion: {company.completion_rate?.toFixed(1)}%
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-indigo-600">
                          {company.total_users}
                        </p>
                        <p className="text-sm text-gray-600">Users</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">
                          {company.total_surveys}
                        </p>
                        <p className="text-sm text-gray-600">Surveys</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination Controls */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 pt-6 mt-6">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onPageChange(pagination.page - 1)}
                    disabled={
                      !pagination.has_previous || loading || paginationLoading
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {paginationLoading && pagination.page > 1 && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
                    )}
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <button
                    onClick={() => onPageChange(pagination.page + 1)}
                    disabled={
                      !pagination.has_next || loading || paginationLoading
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    Next
                    {paginationLoading &&
                      pagination.page < pagination.total_pages && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
                      )}
                  </button>
                </div>
                <div className="text-sm text-gray-600">
                  Showing {companyStats.companies?.length || 0} of{" "}
                  {companyStats.global_stats?.total_companies || 0} companies
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Company Analysis */}
      {companyAnalysis && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative">
          {analysisPaginationLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading...</p>
              </div>
            </div>
          )}
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Company Analysis Report
          </h2>
          <div
            className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${
              analysisPaginationLoading ? "opacity-50" : ""
            }`}
          >
            {companyAnalysis.company_wide_analysis_report?.map(
              (company, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {company.company}
                  </h3>
                  <div className="space-y-4">
                    {Object.entries(company)
                      .filter(([key]) => key !== "company")
                      .map(([metric, value]) => (
                        <div
                          key={metric}
                          className="flex items-center justify-between"
                        >
                          <span className="text-sm font-medium text-gray-600 capitalize">
                            {metric.replace(/_/g, " ")}
                          </span>
                          <div className="flex items-center space-x-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-indigo-600 h-2 rounded-full"
                                style={{ width: `${value}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-semibold text-gray-900 w-12 text-right">
                              {value}
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )
            )}
          </div>

          {/* Pagination Controls */}
          {analysisPagination &&
            (analysisPagination.has_next ||
              analysisPagination.has_previous ||
              analysisPagination.page > 1) && (
              <div className="flex items-center justify-between border-t border-gray-200 pt-6 mt-6">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      onAnalysisPageChange(analysisPagination.page - 1)
                    }
                    disabled={
                      !analysisPagination.has_previous ||
                      loading ||
                      analysisPaginationLoading
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {analysisPagination.page}
                    {analysisPagination.total_pages > 1 &&
                      ` of ${analysisPagination.total_pages}`}
                    {analysisPagination.has_next &&
                      analysisPagination.total_pages <= 1 &&
                      " (more available)"}
                  </span>
                  <button
                    onClick={() =>
                      onAnalysisPageChange(analysisPagination.page + 1)
                    }
                    disabled={
                      !analysisPagination.has_next ||
                      loading ||
                      analysisPaginationLoading
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
                <div className="text-sm text-gray-600">
                  Showing{" "}
                  {companyAnalysis.company_wide_analysis_report?.length || 0}{" "}
                  companies
                  {analysisPagination.has_next && " (more pages available)"}
                </div>
              </div>
            )}
        </div>
      )}

      {/* Company Weekly AI Analytics */}
      {companyWeeklyAI && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative">
          {weeklyAIPaginationLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading...</p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Weekly AI Question Analytics
            </h2>
          </div>
          <div className={weeklyAIPaginationLoading ? "opacity-50" : ""}>
            <WeeklyAIAnalytics data={companyWeeklyAI} />
          </div>

          {/* Pagination Controls */}
          {weeklyAIPagination && weeklyAIPagination.total_pages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 pt-6 mt-6">
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    onWeeklyAIPageChange(weeklyAIPagination.page - 1)
                  }
                  disabled={!weeklyAIPagination.has_previous || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {weeklyAIPagination.page} of{" "}
                  {weeklyAIPagination.total_pages}
                </span>
                <button
                  onClick={() =>
                    onWeeklyAIPageChange(weeklyAIPagination.page + 1)
                  }
                  disabled={
                    !weeklyAIPagination.has_next ||
                    loading ||
                    weeklyAIPaginationLoading
                  }
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Showing page {weeklyAIPagination.page} of{" "}
                {weeklyAIPagination.total_pages}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Company Surveys */}
      {companySurveys && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative">
          {surveysPaginationLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading...</p>
              </div>
            </div>
          )}
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Survey Statistics
          </h2>

          {/* Company Selection Cards */}
          <div
            className={`mb-8 ${surveysPaginationLoading ? "opacity-50" : ""}`}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Select Company to View Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {companySurveys.per_company_stats?.map((company, index) => {
                // Use completion_rate directly from API response
                const completionRate = company.completion_rate || 0;

                return (
                  <div
                    key={index}
                    onClick={() => setSelectedCompany(company)}
                    className={`cursor-pointer rounded-lg p-4 border-2 transition-all duration-200 hover:shadow-lg ${
                      selectedCompany?.company === company.company
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {company.company}
                      </h4>
                      {selectedCompany?.company === company.company && (
                        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                      )}
                    </div>

                    <div className="space-y-2">
                      {/* Progress Bar */}
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Completion Rate</span>
                          <span>{completionRate.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${completionRate}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected Company Charts */}
          {selectedCompany && (
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedCompany.company} - Survey Analytics
                </h3>
                <button
                  onClick={() => setSelectedCompany(null)}
                  className="text-gray-500 hover:text-gray-700 text-sm font-medium"
                >
                  Close Details
                </button>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie Chart */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h4 className="text-md font-medium text-gray-800 mb-4">
                    Survey Status Distribution
                  </h4>
                  <CompanyPieChart data={selectedCompany} />
                </div>

                {/* Bar Chart */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h4 className="text-md font-medium text-gray-800 mb-4">
                    Survey Timeline
                  </h4>
                  <CompanyTimelineChart data={selectedCompany} />
                </div>
              </div>
            </div>
          )}

          {/* Pagination Controls */}
          {surveysPagination && surveysPagination.total_pages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 pt-6 mt-6">
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    onSurveysPageChange(surveysPagination.page - 1)
                  }
                  disabled={!surveysPagination.has_previous || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {surveysPagination.page} of{" "}
                  {surveysPagination.total_pages}
                </span>
                <button
                  onClick={() =>
                    onSurveysPageChange(surveysPagination.page + 1)
                  }
                  disabled={
                    !surveysPagination.has_next ||
                    loading ||
                    surveysPaginationLoading
                  }
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
              <div className="text-sm text-gray-600">
                Showing page {surveysPagination.page} of{" "}
                {surveysPagination.total_pages}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Talent Tab Component
const TalentTab = ({
  talentData,
  talentWeeklyAI,
  talentAllUsers,
  loading,
  error,
  onRefresh,
  selectedTalent,
  setSelectedTalent,
  onUserClick,
  surveyLoading,
  selectedUser,
  setSelectedUser,
  onSurveyClick,
  selectedUserForWeeklyAI,
  onUserSelectionForWeeklyAI,
  onTalentDelete,
  weeklyAILoading,
  talentDataPagination,
  onTalentDataPageChange,
  talentDataPaginationLoading,
  talentAllUsersPagination,
  onTalentAllUsersPageChange,
  talentAllUsersPaginationLoading,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading talent data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-red-600 mb-4">
          <svg
            className="w-12 h-12 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-red-800 mb-2">
          Error Loading Data
        </h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={onRefresh}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Talent Overview with All Users Data */}
      {talentAllUsers && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative">
          {talentAllUsersPaginationLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading...</p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Talent Overview
            </h2>
            <button
              onClick={onRefresh}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              Refresh
            </button>
          </div>

          {/* Global Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-blue-600">
                    Total Users
                  </p>
                  <p className="text-2xl font-bold text-blue-900">
                    {talentAllUsers.total_users || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-green-600">
                    Total Surveys
                  </p>
                  <p className="text-2xl font-bold text-green-900">
                    {talentAllUsers.total_surveys || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg
                    className="w-6 h-6 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-purple-600">
                    Completion Rate
                  </p>
                  <p className="text-2xl font-bold text-purple-900">
                    {talentAllUsers.completion_rate?.toFixed(1) || 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* User Cards */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">User Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {talentAllUsers.users?.map((user, index) => (
                <div
                  key={user.user_id}
                  onClick={() => {
                    setSelectedUser(user);
                    onUserClick(user.user_id);
                  }}
                  className={`cursor-pointer rounded-lg p-4 border-2 transition-all duration-200 hover:shadow-lg ${
                    selectedTalent?.user_id === user.user_id
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  } ${surveyLoading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {user.full_name}
                    </h4>
                    <div className="flex items-center space-x-2">
                      {surveyLoading &&
                        selectedUser?.user_id === user.user_id && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                        )}
                      {selectedTalent?.user_id === user.user_id && (
                        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">{user.email}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Surveys:</span>
                      <span className="text-lg font-bold text-indigo-600">
                        {user.surveys?.length || 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination Controls */}
          {talentAllUsersPagination &&
            (talentAllUsersPagination.has_next ||
              talentAllUsersPagination.has_previous ||
              talentAllUsersPagination.page > 1) && (
              <div className="flex items-center justify-between border-t border-gray-200 pt-6 mt-6">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      onTalentAllUsersPageChange(
                        talentAllUsersPagination.page - 1
                      )
                    }
                    disabled={
                      !talentAllUsersPagination.has_previous ||
                      loading ||
                      talentAllUsersPaginationLoading
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {talentAllUsersPagination.page}
                    {/* Show total pages if provided by API, otherwise show "more available" if hasNext */}
                    {talentAllUsersPagination._total_pages_provided
                      ? talentAllUsersPagination.total_pages > 1 &&
                        ` of ${talentAllUsersPagination.total_pages}`
                      : talentAllUsersPagination.has_next &&
                        " (more available)"}
                  </span>
                  <button
                    onClick={() =>
                      onTalentAllUsersPageChange(
                        talentAllUsersPagination.page + 1
                      )
                    }
                    disabled={
                      !talentAllUsersPagination.has_next ||
                      loading ||
                      talentAllUsersPaginationLoading
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
                <div className="text-sm text-gray-600">
                  Showing {talentAllUsers.users?.length || 0} users
                  {talentAllUsersPagination.has_next &&
                    " (more pages available)"}
                </div>
              </div>
            )}
        </div>
      )}

      {/* Legacy Talent Analysis Report */}
      {talentData && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative">
          {talentDataPaginationLoading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading...</p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Talent Analysis Report
            </h2>
            <button
              onClick={onRefresh}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              Refresh
            </button>
          </div>

          {/* Talent Cards */}
          <div className={talentDataPaginationLoading ? "opacity-50" : ""}>
            <TalentCards
              talentData={talentData}
              onTalentSelect={setSelectedTalent}
              selectedTalent={selectedTalent}
              onTalentDelete={onTalentDelete}
            />
          </div>

          {/* Pagination Controls */}
          {talentDataPagination &&
            (talentDataPagination.has_next ||
              talentDataPagination.has_previous ||
              talentDataPagination.page > 1) && (
              <div className="flex items-center justify-between border-t border-gray-200 pt-6 mt-6">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      onTalentDataPageChange(talentDataPagination.page - 1)
                    }
                    disabled={
                      !talentDataPagination.has_previous ||
                      loading ||
                      talentDataPaginationLoading
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {talentDataPagination.page}
                    {/* Show total pages if provided by API, otherwise show "more available" if hasNext */}
                    {talentDataPagination._total_pages_provided
                      ? talentDataPagination.total_pages > 1 &&
                        ` of ${talentDataPagination.total_pages}`
                      : talentDataPagination.has_next && " (more available)"}
                  </span>
                  <button
                    onClick={() =>
                      onTalentDataPageChange(talentDataPagination.page + 1)
                    }
                    disabled={
                      !talentDataPagination.has_next ||
                      loading ||
                      talentDataPaginationLoading
                    }
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
                <div className="text-sm text-gray-600">
                  Showing {talentData.data?.length || 0} talents
                  {talentDataPagination.has_next && " (more pages available)"}
                </div>
              </div>
            )}
        </div>
      )}

      {selectedTalent && (
        <TalentDetailedReports
          selectedTalent={selectedTalent}
          onClose={() => setSelectedTalent(null)}
        />
      )}

      {/* User Selection for Weekly AI Analytics */}
      {talentAllUsers &&
        talentAllUsers.users &&
        talentAllUsers.users.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Select User for Weekly AI Analytics
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {talentAllUsers.users.map((user) => (
                <div
                  key={user.user_id}
                  onClick={() => onUserSelectionForWeeklyAI(user)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedUserForWeeklyAI?.user_id === user.user_id
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="text-center">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-indigo-600 font-semibold text-lg">
                        {user.full_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <h3 className="font-medium text-gray-900 text-sm mb-1">
                      {user.full_name}
                    </h3>
                    <p className="text-xs text-gray-500 mb-2">{user.email}</p>
                    <div className="text-xs text-gray-400">
                      {user.surveys.length} surveys
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Weekly AI Analytics Section */}
      {(talentWeeklyAI || weeklyAILoading) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Weekly AI Question Analytics
            {selectedUserForWeeklyAI && (
              <span className="text-sm font-normal text-gray-600 ml-2">
                - {selectedUserForWeeklyAI.full_name}
              </span>
            )}
          </h2>

          {weeklyAILoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading Weekly AI Analytics...</p>
              </div>
            </div>
          ) : (
            <TalentWeeklyAIAnalytics data={talentWeeklyAI} />
          )}
        </div>
      )}
    </div>
  );
};

// Chart Components for Individual Company Survey Statistics

// Company Pie Chart Component
const CompanyPieChart = ({ data }) => {
  if (!data || !data.surveys) return null;

  // Calculate filled, expired, and created surveys for this company
  let totalFilled = 0;
  let totalExpired = 0;
  let totalCreated = 0;

  data.surveys.forEach((survey) => {
    totalFilled += survey.surveys_filled;
    totalExpired += survey.surveys_expired;
    totalCreated += survey.surveys_created;
  });

  // Merge Expired and Created into "Non Filled"
  const totalNonFilled = totalExpired + totalCreated;

  const pieData = [
    { name: "Filled", value: totalFilled, color: "#10B981" },
    { name: "Non Filled", value: totalNonFilled, color: "#EF4444" },
  ];

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={pieData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) =>
            `${name} ${(percent * 100).toFixed(0)}%`
          }
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {pieData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

// Company Timeline Chart Component
const CompanyTimelineChart = ({ data }) => {
  if (!data || !data.surveys) return null;

  // Create timeline data grouped by date
  const timelineMap = new Map();

  data.surveys.forEach((survey) => {
    const date = survey.survey_date;
    if (!timelineMap.has(date)) {
      timelineMap.set(date, { date, filled: 0, nonFilled: 0 });
    }
    const entry = timelineMap.get(date);
    entry.filled += survey.surveys_filled;
    // Merge expired and created into nonFilled
    entry.nonFilled += survey.surveys_expired + survey.surveys_created;
  });

  const timelineData = Array.from(timelineMap.values()).sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={timelineData}
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="date"
          stroke="#64748b"
          fontSize={12}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis stroke="#64748b" fontSize={12} />
        <Tooltip
          contentStyle={{
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        />
        <Legend />
        <Bar
          dataKey="nonFilled"
          stackId="a"
          fill="#EF4444"
          name="Non Filled"
          radius={[0, 0, 0, 0]}
        />
        <Bar
          dataKey="filled"
          stackId="a"
          fill="#10B981"
          name="Filled"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Weekly AI Analytics Component
const WeeklyAIAnalytics = ({ data }) => {
  // Track which companies have expanded weeks (company name -> boolean)
  const [expandedCompanies, setExpandedCompanies] = useState({});

  if (!data || !data.company_wide_weekly_ai_question_analytics) return null;

  const analyticsData = data.company_wide_weekly_ai_question_analytics;

  // Sort by week_start date (latest first)
  const sortedWeeks = [...analyticsData].sort(
    (a, b) => new Date(b.week_start) - new Date(a.week_start)
  );
  const latestWeek = sortedWeeks[0];
  const otherWeeks = sortedWeeks.slice(1);

  // Group weeks by company to find which companies have multiple weeks
  const companyWeeksMap = new Map();
  sortedWeeks.forEach((weekData) => {
    weekData.companies.forEach((company) => {
      const companyName = company.company;
      if (!companyWeeksMap.has(companyName)) {
        companyWeeksMap.set(companyName, []);
      }
      companyWeeksMap.get(companyName).push({
        week: weekData.week_start,
        companyData: company,
      });
    });
  });

  // Toggle company expansion
  const toggleCompanyExpansion = (companyName) => {
    setExpandedCompanies((prev) => ({
      ...prev,
      [companyName]: !prev[companyName],
    }));
  };

  return (
    <div className="space-y-6">
      {/* Show latest week */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Week of {latestWeek.week_start}
          </h3>
          <span className="text-sm text-gray-500">
            Generated: {new Date(data.generated_at).toLocaleDateString()}
          </span>
        </div>

        <div className="w-full">
          {latestWeek.companies.map((company, companyIndex) => {
            const companyName = company.company;
            const companyWeeks = companyWeeksMap.get(companyName) || [];
            const hasMultipleWeeks = companyWeeks.length > 1;
            const isExpanded = expandedCompanies[companyName] || false;
            const otherCompanyWeeks = companyWeeks.filter(
              (cw) => cw.week !== latestWeek.week_start
            );

            return (
              <div key={companyIndex} className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 w-full relative">
                  {/* Show "See More" button inside the div at top-right if it has multiple weeks */}
                  {hasMultipleWeeks && !isExpanded && (
                    <div className="absolute top-4 right-4">
                      <button
                        onClick={() => toggleCompanyExpansion(companyName)}
                        className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        See More ({otherCompanyWeeks.length})
                      </button>
                    </div>
                  )}
                  <h4 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                    {company.company}
                  </h4>

                  {/* AI vs Non-AI Charts in same row */}
                  <div className="flex flex-col xl:flex-row gap-6 w-full">
                    {/* AI Generated Chart */}
                    <div className="flex-1 bg-white rounded-lg p-4 border border-gray-200 min-w-0 shadow-sm">
                      <h5 className="text-base font-medium text-gray-700 mb-3 text-center">
                        Upstart AEC AI
                      </h5>
                      <div className="w-full h-full">
                        <AIQuestionChart
                          data={company.ai_generated}
                          color="#3B82F6"
                        />
                      </div>
                    </div>

                    {/* Non-AI Chart */}
                    <div className="flex-1 bg-white rounded-lg p-4 border border-gray-200 min-w-0 shadow-sm">
                      <h5 className="text-base font-medium text-gray-700 mb-3 text-center">
                        Upstart AEC Questionnaire
                      </h5>
                      <div className="w-full h-full">
                        <AIQuestionChart
                          data={company.non_ai}
                          color="#10B981"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Show other weeks for this company if expanded */}
                {isExpanded && otherCompanyWeeks.length > 0 && (
                  <div className="space-y-4 pl-4 border-l-2 border-gray-300">
                    {otherCompanyWeeks.map((companyWeek, weekIdx) => {
                      const weekData = sortedWeeks.find(
                        (w) => w.week_start === companyWeek.week
                      );
                      if (!weekData) return null;

                      return (
                        <div key={weekIdx} className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-base font-medium text-gray-700">
                              Week of {companyWeek.week}
                            </h3>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 w-full relative">
                            {/* Show "Show Less" button only on the last expanded week */}
                            {weekIdx === otherCompanyWeeks.length - 1 && (
                              <div className="absolute top-4 right-4">
                                <button
                                  onClick={() =>
                                    toggleCompanyExpansion(companyName)
                                  }
                                  className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
                                >
                                  Show Less
                                </button>
                              </div>
                            )}
                            <h4 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                              {companyName}
                            </h4>

                            {/* AI vs Non-AI Charts in same row */}
                            <div className="flex flex-col xl:flex-row gap-6 w-full">
                              {/* AI Generated Chart */}
                              <div className="flex-1 bg-white rounded-lg p-4 border border-gray-200 min-w-0 shadow-sm">
                                <h5 className="text-base font-medium text-gray-700 mb-3 text-center">
                                  Upstart AEC AI
                                </h5>
                                <div className="w-full h-full">
                                  <AIQuestionChart
                                    data={companyWeek.companyData.ai_generated}
                                    color="#3B82F6"
                                  />
                                </div>
                              </div>

                              {/* Non-AI Chart */}
                              <div className="flex-1 bg-white rounded-lg p-4 border border-gray-200 min-w-0 shadow-sm">
                                <h5 className="text-base font-medium text-gray-700 mb-3 text-center">
                                  Upstart AEC Questionnaire
                                </h5>
                                <div className="w-full h-full">
                                  <AIQuestionChart
                                    data={companyWeek.companyData.non_ai}
                                    color="#10B981"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// AI Question Chart Component
const AIQuestionChart = ({ data, color }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500">
        No data available
      </div>
    );
  }

  // Transform data for bar chart
  const chartData = data.map((item, index) => ({
    pillar:
      item.pillar.length > 18
        ? item.pillar.substring(0, 18) + "..."
        : item.pillar,
    fullPillar: item.pillar,
    value: item.value,
    index: index + 1,
  }));

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart
        data={chartData}
        margin={{ top: 20, right: 15, left: 15, bottom: 70 }}
        barCategoryGap="20%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="pillar"
          stroke="#64748b"
          fontSize={11}
          angle={-45}
          textAnchor="end"
          height={80}
          interval={0}
        />
        <YAxis stroke="#64748b" fontSize={12} domain={[0, 100]} tickCount={5} />
        <Tooltip
          contentStyle={{
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          labelFormatter={(label, payload) => {
            if (payload && payload[0]) {
              return payload[0].payload.fullPillar;
            }
            return label;
          }}
          formatter={(value) => [`${value}%`, "Score"]}
        />
        <Bar
          dataKey="value"
          fill={color}
          radius={[4, 4, 0, 0]}
          maxBarSize={100}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Talent Weekly AI Analytics Component
const TalentWeeklyAIAnalytics = ({ data }) => {
  if (!data || !data.weekly_ai_question_analytics) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Analytics Data Available
          </h3>
          <p className="text-gray-500 mb-4">
            This user hasn't completed any surveys yet.
          </p>
          <p className="text-sm text-gray-400">
            Analytics will appear here once surveys are completed.
          </p>
        </div>
      </div>
    );
  }

  const analyticsData = data.weekly_ai_question_analytics;

  // Check if analytics data is empty
  if (!analyticsData || analyticsData.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Analytics Data Available
          </h3>
          <p className="text-gray-500 mb-4">
            This user hasn't completed any surveys yet.
          </p>
          <p className="text-sm text-gray-400">
            Analytics will appear here once surveys are completed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {analyticsData.map((weekData, weekIndex) => (
        <div key={weekIndex} className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Week of {weekData.week_start}
            </h3>
            <span className="text-sm text-gray-500">
              Generated: {new Date(data.generated_at).toLocaleDateString()}
            </span>
          </div>

          <div className="w-full">
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 w-full">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                Weekly AI Question Analytics
              </h4>

              {/* AI vs Non-AI Charts in same row */}
              <div className="flex flex-col xl:flex-row gap-6 w-full">
                {/* AI Generated Chart */}
                <div className="flex-1 bg-white rounded-lg p-4 border border-gray-200 min-w-0 shadow-sm">
                  <h5 className="text-base font-medium text-gray-700 mb-3 text-center">
                    Upstart AEC AI
                  </h5>
                  <div className="w-full h-full">
                    <AIQuestionChart
                      data={weekData.ai_generated}
                      color="#3B82F6"
                    />
                  </div>
                </div>

                {/* Non-AI Chart */}
                <div className="flex-1 bg-white rounded-lg p-4 border border-gray-200 min-w-0 shadow-sm">
                  <h5 className="text-base font-medium text-gray-700 mb-3 text-center">
                    Upstart AEC Questionnaire
                  </h5>
                  <div className="w-full h-full">
                    <AIQuestionChart data={weekData.non_ai} color="#10B981" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Survey Details Modal Component
const SurveyDetailsModal = ({
  surveyDetails,
  selectedUser,
  onClose,
  loading,
  onSurveyClick,
  loadingSurveyId,
}) => {
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading survey details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!surveyDetails || !selectedUser) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Survey Details</h2>
            <p className="text-gray-600 mt-1">
              {selectedUser.full_name} ({selectedUser.email})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Survey Cards */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Survey List
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {surveyDetails.surveys?.map((survey, index) => (
                <div
                  key={survey.survey_id}
                  className={`rounded-lg p-6 border-2 transition-all duration-200 hover:shadow-md ${
                    survey.expired
                      ? "border-red-200 bg-red-50"
                      : "border-green-200 bg-green-50"
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">
                        {survey.topic}
                      </h4>
                      <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                        {survey.description}
                      </p>
                    </div>
                    <div className="ml-4 flex flex-col items-end space-y-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          survey.expired
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {survey.expired ? "Expired" : "Active"}
                      </span>
                      <span className="text-xs text-gray-500">
                        {survey.estimated_time}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-4">
                      <span>
                        <strong>Created:</strong>{" "}
                        {new Date(survey.created_at).toLocaleDateString()}
                      </span>
                      <span>
                        <strong>Time:</strong>{" "}
                        {new Date(survey.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">
                      ID: {survey.survey_id.substring(0, 8)}...
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() =>
                        onSurveyClick(survey.survey_id, selectedUser.user_id)
                      }
                      disabled={loadingSurveyId === survey.survey_id}
                      className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center space-x-2 ${
                        loadingSurveyId === survey.survey_id
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-indigo-600 hover:bg-indigo-700"
                      } text-white`}
                    >
                      {loadingSurveyId === survey.survey_id && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      )}
                      <span>View Survey</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Generated At Info */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Generated at:{" "}
              {new Date(surveyDetails.generated_at).toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Survey Data Modal Component
const SurveyDataModal = ({ surveyData, selectedSurvey, onClose, loading }) => {
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading survey data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!surveyData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {surveyData.data?.topic}
            </h2>
            <p className="text-gray-600 mt-1">
              Survey ID: {surveyData.survey_id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Survey Info */}
          <div className="mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Survey Description
              </h3>
              <p className="text-blue-800 leading-relaxed">
                {surveyData.data?.description}
              </p>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-blue-600">
                    <strong>Estimated Time:</strong>{" "}
                    {surveyData.data?.estimated_time}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      surveyData.expired
                        ? "bg-red-100 text-red-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {surveyData.expired ? "Expired" : "Active"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">
              Survey Questions ({surveyData.data?.questions?.length || 0})
            </h3>

            <div className="space-y-4">
              {surveyData.data?.questions?.map((question, index) => (
                <div
                  key={question.question_id}
                  className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2 py-1 rounded">
                          Q{question.question_id}
                        </span>
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded ${
                            question.required
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {question.required ? "Required" : "Optional"}
                        </span>
                        {question.ai_generated && (
                          <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded">
                            AI Generated
                          </span>
                        )}
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 mb-3">
                        {question.question_text}
                      </h4>
                    </div>
                    <div className="ml-4">
                      <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded">
                        {question.question_type.replace("_", " ").toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Question Options */}
                  {question.options && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        Options:
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {question.options.map((option, optIndex) => (
                          <div
                            key={optIndex}
                            className="bg-gray-50 border border-gray-200 rounded p-3"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-gray-800">
                                {option.text}
                              </span>
                              <span className="text-gray-500 text-sm">
                                Value: {option.value}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Rating Scale */}
                  {question.question_type === "rating" && (
                    <div className="mt-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Scale:</span>
                        <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded">
                          {question.min_value} - {question.max_value}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Answer Display */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      <strong>Answer:</strong>
                      <span className="ml-2 text-gray-800">
                        {question.answer || "No answer provided"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Generated At Info */}
          <div className="mt-8 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Survey retrieved at: {new Date().toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;
